import React, { useEffect, useRef, useState } from 'react';
import './voice.style.scss';
import {
    FileAttachment,
    FileAttachmentStatusEnum,
    FileAttachmentTypeEnum,
    FileUploadErrType,
    ReduxState,
    Response,
    TextDirection,
} from 'interface';
import { toast } from 'react-toastify';
import { connect, ConnectedProps } from 'react-redux';
import { upload } from 'scripts/api';
import { __VoiceProps, __VoiceRef } from './voice.interface';
import { Spinner } from 'react-bootstrap';
import HintText from '../hintText/hintText.index';
import QuestionImage from '../questionImage/questionImage.index';
import { UploadFileErrToast } from 'data/errorCodeToast';
import { API } from 'data';
import { FileManager } from 'scripts/fileManager';
import { MessageBridge } from 'scripts/messageBridge';
import { Player } from '@lottiefiles/react-lottie-player';
import Animation from './voice.animation.json';
import ENV from 'env.json';
import { extractTitle } from 'scripts/string';
export interface SLPVoiceRef extends __VoiceRef {}

const SLPVoice: React.ForwardRefRenderFunction<__VoiceRef, ConnectedProps<typeof connector> & __VoiceProps> = function (
    props: ConnectedProps<typeof connector> & __VoiceProps,
    ref,
) {
    React.useImperativeHandle(ref, () => ({
        getValue: () => immediateValue.current,
        setError: (msg: string) => setError(msg),
        isRecording: status === 'record',
        uploadFile: () => {
            return new Promise((resolve) => {
                onDone();
                immediateValue.current
                    ? onFileUpload().then((res) => resolve(res))
                    : mediaRecorder.current?.addEventListener('dataavailable', (event) => {
                          const file = new File([event.data], genName());
                          immediateValue.current = file2Attachment(file, URL.createObjectURL(file), 0);
                          onFileUpload().then((res) => resolve(res));
                      });
            });
        },
    }));
    const lang = props.lang.utilities.SLPVoice;
    const [error, setError] = useState<string>();
    const labelRef = useRef<HTMLLabelElement>(null);
    const mediaRecorder = useRef<MediaRecorder>();
    const playerRef = useRef<Player>(null);
    const [file, setFile] = useState<FileAttachment | undefined>(props.defaultValue ? props.defaultValue : undefined);
    const [offlineFile, setOfflineFile] = useState<string>();
    const immediateValue = useRef<FileAttachment | undefined>(file);
    let timeOut: NodeJS.Timeout | undefined = undefined;
    const maxTime = props.maxTime ? props.maxTime : 60;
    const [status, setStatus] = useState<'start' | 'record' | 'delete'>(file ? 'delete' : 'start');
    const [uploading, setUploading] = useState(false);
    const [timer, setTimer] = useState(0);
    const counterNumber = useRef(0);
    const counterRef = useRef<any>();

    useEffect(() => {
        if (props.defaultValue?.file) {
            FileManager.GetFile(props.defaultValue.file).then((res) => {
                if (res) FileManager.RenderFile(res).then((res2) => setOfflineFile(res2));
            });
        }
    }, []);

    useEffect(() => {
        if (labelRef.current && props.label) labelRef.current.innerHTML = extractTitle(props.label, false);
    }, [props.label]);

    function file2Attachment(e: File, url: string, id: string | number): FileAttachment {
        const titles = e.name.split('.');
        const ext = titles[1];
        return {
            file: url,
            id: id,
            created_at: new Date().toISOString(),
            workspace: props.ws ?? 0,
            extension: ext,
            name: e.name,
            filename: e.name,
            offset: 0,
            status: FileAttachmentStatusEnum.complete,
            type: FileAttachmentTypeEnum.audio,
            size: e.size / 1024 / 1024,
        };
    }

    function genName() {
        return (
            'voice_answer_' +
            new Date().getFullYear() +
            '_' +
            (new Date().getMonth() + 1) +
            '_' +
            new Date().getDate() +
            '_' +
            new Date().getHours() +
            '_' +
            new Date().getMinutes() +
            '_' +
            new Date().getSeconds() +
            '.mp3'
        );
    }

    const onFileUpload: () => Promise<FileAttachment | undefined> = () => {
        const changeFile = (newFile: FileAttachment) => {
            immediateValue.current = newFile;
            setFile(immediateValue.current);
            if (props.onChange) props.onChange(newFile);
            setError(undefined);
        };
        return new Promise((resolve) => {
            if (immediateValue.current?.file && !immediateValue.current.id && !uploading) {
                fetch(immediateValue.current?.file).then((r) => {
                    r.blob()
                        .then((e) => {
                            setUploading(true);
                            const FILE = new File([e], genName());
                            const form = new FormData();
                            form.append('file', FILE);
                            form.append('alt', FILE.name);
                            form.append('filename', FILE.name);
                            form.append('name', FILE.name);
                            form.append('type', FileAttachmentTypeEnum.audio.toString());
                            form.append('workspace', props.ws.toString());
                            upload<FileAttachment>(API.upload.file, form, false)
                                .then((res) => {
                                    changeFile(res.data);
                                    resolve(res.data);
                                })
                                .catch((res: Response<FileUploadErrType>) => {
                                    if (res.status >= 500) {
                                        resolve(undefined);
                                        toast.error(props.lang.serverErr);
                                    } else if (res.status >= 400) {
                                        resolve(undefined);
                                        UploadFileErrToast(res.data);
                                    } else {
                                        // offline mode
                                        const id = FileManager.StoreFile(FILE);
                                        const newFile = file2Attachment(FILE, id, id);
                                        resolve(newFile);
                                        changeFile(newFile);
                                        setOfflineFile(r.url);
                                    }
                                })
                                .finally(() => setUploading(false));
                        })
                        .catch(() => resolve(undefined));
                });
            } else resolve(undefined);
        });
    };

    const onActive = () => {
        const internalProcess = () => {
            console.log(Boolean(navigator.mediaDevices));
            navigator.mediaDevices
                .getUserMedia({ audio: true })
                .then((stream) => {
                    console.log('--------', stream);
                    setStatus('record');
                    playerRef.current?.play();
                    setError(undefined);
                    counterRef.current = setInterval(() => {
                        counterNumber.current = counterNumber.current + 1;
                        setTimer(counterNumber.current);
                    }, 1000);
                    mediaRecorder.current = new MediaRecorder(stream);
                    mediaRecorder.current.start();
                    mediaRecorder.current.addEventListener('dataavailable', (event) => {
                        const file = new File([event.data], genName());
                        const newFile = file2Attachment(file, URL.createObjectURL(file), 0);
                        setFile(newFile);
                        immediateValue.current = newFile;
                        onDone();
                    });
                    if (maxTime) timeOut = setTimeout(onDone, maxTime * 1000);
                    else timeOut = undefined;
                })
                .catch((e) => {
                    console.log(e);
                    toast.error(lang.noPermission);
                });
        };
        if (ENV.mode !== 'production') {
            internalProcess();
        } else {
            MessageBridge.request('ASK_PERMISSION_FROM_USER', {
                key: 'RECORD_AUDIO',
                title: 'درخواست دسترسی به میکروفن',
                message: 'ما برای دریافت پاسخ شما به دسترسی میکروفن احتیاج داریم.',
                // buttonNeutral: string,
                buttonPositive: 'تایید',
                // buttonNegative: 'عدم تایید',
            }).then((res: 'GRANTED' | 'DENIED' | 'NEVER_ASK_AGAIN') => {
                if (res !== 'GRANTED') {
                    toast.error('برای ضبط صدا نیاز به اجازه دسترسی به میکروفن است');
                } else {
                    internalProcess();
                }
            });
        }
    };
    const onDelete = () => {
        counterNumber.current = 0;
        setTimer(0);
        setStatus('start');
        setFile(undefined);
        immediateValue.current = undefined;
        mediaRecorder.current = undefined;
        if (props.onChange) props.onChange(undefined);
    };

    const onDone = () => {
        if (mediaRecorder.current?.state === 'recording') {
            playerRef.current?.stop();
            counterNumber.current = 0;
            clearInterval(counterRef.current);
            setError(undefined);
            timeOut = undefined;
            setStatus('delete');
            mediaRecorder.current?.stop();
            if (props.onDrop) props.onDrop();
        }
    };

    return (
        <div className={`ucheck-SLP-voice-attachment  ${props.dir === TextDirection.rtl ? 'rtl-text' : ''}`}>
            <span className="d-flex">
                {props.required && <span className="required-star">*</span>}
                <span className="counter-span">{props.counter ? props.counter + '- ' : ''}</span>
                <label ref={labelRef} style={{ color: props.labelColor }} />
            </span>
            <QuestionImage src={props.image?.file} />
            <HintText value={props.hint} />
            {status === 'start' || status === 'record' ? (
                <div className="main-controller">
                    <Player
                        ref={playerRef}
                        autoplay={false}
                        loop={true}
                        controls={false}
                        src={Animation}
                        style={{ height: '70px', width: '70px' }}
                    ></Player>
                    <div
                        onClick={() => {
                            console.log('now', status);
                            if (status === 'start') onActive();
                            else onDone();
                        }}
                        className="event-trigger"
                    ></div>
                </div>
            ) : null}
            {status !== 'start' && status !== 'record' ? (
                <div className="file-input">
                    <audio src={file?.file} controls className={`player ${file ? '' : 'd-none'}`} />
                    <span className={`material-icons-outlined mic delete`} onClick={onDelete}>
                        delete
                    </span>
                </div>
            ) : null}
            {status === 'record' ? (
                <div className="counter-time">
                    {props.maxTime ? (
                        <>
                            <span>{new Date(props.maxTime * 1000).toISOString().substr(15, 4)}</span>/
                        </>
                    ) : null}
                    <span>{new Date(timer * 1000).toISOString().substr(15, 4)}</span>
                </div>
            ) : null}
            {error && <p className="input-error">{error}</p>}
        </div>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPVoice));
